/* eslint-disable @typescript-eslint/no-explicit-any */
import type { JSX, ReactNode } from 'react';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Dialog as MuiDialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography
} from '@mui/material';

import { withStore } from '../../store';
import { closeDialog } from '../../store/dialog';

interface DialogConfig {
  open?: boolean;
  titleKey?: string;
  subtitleKey?: string;
  bodyKey?: string;
  title?: ReactNode;
  subtitle?: ReactNode;
  body?: ReactNode;
  primaryBtnTitleKey?: string;
  secondaryBtnTitleKey?: string;
  primaryBtnTitle?: ReactNode;
  secondaryBtnTitle?: ReactNode;
  primaryEvent?: (store: any, dispatch: any) => void;
  secondaryEvent?: (store: any, dispatch: any) => void;
  hidePrimaryBtn?: boolean;
  hideSecondaryBtn?: boolean;
  disablePrimary?: (store: any) => boolean;
  dontCloseOnClickOut?: boolean;
  showCloseIcon?: boolean;
  svg?: string;
  height?: number | string;
  MainComp?: React.ComponentType<{ dialog: DialogConfig }>;
  autoSaveError?: boolean;
  [key: string]: any;
}

interface DialogProps {
  store: {
    dialog: DialogConfig;
    [key: string]: any;
  };
  dispatch: (action: any) => void;
}

function generateDialogObject(
  dialog: DialogConfig,
  t: (key: string) => string
): DialogConfig {
  const {
    subtitleKey,
    titleKey,
    subtitle,
    bodyKey,
    body,
    primaryBtnTitleKey,
    secondaryBtnTitleKey
  } = dialog;

  return {
    ...dialog,
    title: titleKey ? t(titleKey) : dialog.title,
    subtitle: subtitleKey ? t(subtitleKey) : subtitle,
    body: bodyKey ? t(bodyKey) : body,
    primaryBtnTitle: primaryBtnTitleKey
      ? t(primaryBtnTitleKey)
      : 'Done',
    secondaryBtnTitle: secondaryBtnTitleKey
      ? t(secondaryBtnTitleKey)
      : 'Cancel'
  };
}

function Dialog({
  store,
  dispatch
}: DialogProps): JSX.Element | null {
  const { t } = useTranslation();
  const { dialog: unformattedDialog } = store;
  const { autoSaveError = false } =
    unformattedDialog || {};

  const dialog = generateDialogObject(
    unformattedDialog,
    t
  );

  const close = () => dispatch(closeDialog());

  const handleSecondary = () => {
    close();
    if (dialog.secondaryEvent) {
      dialog.secondaryEvent(store, dispatch);
    }
  };

  const handlePrimary = () => {
    close();
    if (dialog.primaryEvent) {
      dialog.primaryEvent(store, dispatch);
    }
  };

  if (!dialog.open) {
    return null;
  }

  const MainComp = dialog.MainComp;

  const actionsSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--corner-radius-md)',
    flexDirection: {
      xs: 'column-reverse',
      sm: 'row'
    },
    width: '100%'
  };

  return (
    <MuiDialog
      open
      onClose={(_, reason) => {
        if (
          dialog.dontCloseOnClickOut &&
          reason === 'backdropClick'
        )
          return;
        close();
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor:
              'rgba(0, 0, 0, 0.5)'
          }
        },
        paper: {
          sx: {
            position: 'relative',
            backgroundColor: '#fff',
            boxShadow:
              '1px 1px 10px rgba(0, 0, 0, 0.1)',
            borderRadius:
              'var(--corner-radius-md)',
            border: '1px solid var(--neutral-300)',
            px: 2,
            py: 3,
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            width: '100%',
            minWidth: { xs: 'auto', sm: 400 },
            maxWidth: {
              xs: '100%',
              sm: 550,
              md: 675
            },
            m: { xs: 2, sm: 0 }
          }
        }
      }}
      sx={{
        '& .MuiDialog-container': {
          pt: '6rem',
          alignItems: 'flex-start'
        }
      }}
    >
      {MainComp ? (
        <MainComp dialog={dialog} />
      ) : (
        <Fragment>
          {dialog.showCloseIcon && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end'
              }}
            >
              <IconButton
                aria-label="close"
                onClick={handleSecondary}
                sx={{ mr: 1.25 }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          )}

          {dialog.svg && (
            <Box
              component="img"
              src={dialog.svg}
              alt=""
              sx={{
                height: dialog.height,
                marginBottom:
                  'var(--spacing-4xl)',
                alignSelf: 'center',
                width: '240px',
                aspectRatio: '77/60'
              }}
            />
          )}

          <DialogContent
            sx={{ p: 0, overflow: 'visible' }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Typography
                sx={{
                  fontSize: {
                    xs: '28px !important',
                    sm: '32px !important'
                  },
                  lineHeight: {
                    xs: '36px !important',
                    sm: '42px !important'
                  },
                  m: 0,
                  textAlign: 'center',
                  fontWeight: 700
                }}
              >
                {dialog.title}
              </Typography>

              {dialog.subtitle && (
                <Typography
                  sx={{
                    fontSize: autoSaveError
                      ? '16px'
                      : {
                          xs: '18px !important',
                          sm: '20px !important'
                        },
                    lineHeight: autoSaveError
                      ? '24px'
                      : {
                          xs: '24px !important',
                          sm: '26px !important'
                        },
                    px: autoSaveError ? '16px' : 0,
                    fontWeight: autoSaveError
                      ? 400
                      : 700,
                    textAlign: 'center',
                    marginTop:
                      'var(--corner-radius-lg)'
                  }}
                >
                  {dialog.subtitle}
                </Typography>
              )}
            </Box>

            {dialog.body && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Typography
                  sx={{
                    fontSize: {
                      xs: '18px !important',
                      sm: '20px !important'
                    },
                    lineHeight: {
                      xs: '24px !important',
                      sm: '26px !important'
                    },
                    fontWeight: 700,
                    textAlign: 'center',
                    marginTop:
                      'var(--corner-radius-lg)'
                  }}
                >
                  {dialog.body}
                </Typography>
              </Box>
            )}
          </DialogContent>
        </Fragment>
      )}

      <DialogActions
        sx={{
          p: 0,
          justifyContent: 'center',
          width: '100%',
          marginTop: 'var(--spacing-4xl)'
        }}
      >
        <Box sx={actionsSx}>
          {!dialog.hideSecondaryBtn && (
            <Button
              variant="outlined"
              onClick={handleSecondary}
              sx={{
                height: '40px',
                width: {
                  xs: '100%',
                  sm: 'auto'
                }
              }}
            >
              {dialog.secondaryBtnTitle}
            </Button>
          )}

          {!dialog.hidePrimaryBtn && (
            <Button
              variant="contained"
              onClick={handlePrimary}
              disabled={
                dialog.disablePrimary &&
                dialog.disablePrimary(store)
              }
              sx={{
                height: '40px',
                width: {
                  xs: '100%',
                  sm: 'auto'
                }
              }}
            >
              {dialog.primaryBtnTitle}
            </Button>
          )}
        </Box>
      </DialogActions>
    </MuiDialog>
  );
}

export default withStore(Dialog);